import { atom } from "jotai";
import type { Plugin } from "prosemirror-state";

import { parsePage } from "@moment/api-collab/markdown";
import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { $docsApi } from "../docsapi";
import { type DocsListItem, docsListItemRemoteEntry } from "../document-list";
import { $loadEditorState, $setEditorStateError } from "./editor-state.atom";
import { PmRemoteLoadError } from "./errors";

const logger = new Logger("load-editor-state-from-remote");

export interface LoadEditorStateFromRemoteInput {
	key: string;
	document: DocsListItem;
	pageId: string;
	pagePath?: string;
	ref: string;
	plugins: Plugin[];
	ssr: boolean;
}

/**
 * Loads an editor state from the remote DocsAPI (git.moment.dev).
 */
export const $loadEditorStateFromRemote = atom(
	null,
	async (get, set, input: LoadEditorStateFromRemoteInput) => {
		const { key, document, pageId, pagePath, ref, plugins, ssr } = input;
		const documentId = document.id;

		try {
			// Get the remote entry from the document.
			const workspaceName = docsListItemRemoteEntry(document)?.workspace.name;
			if (!workspaceName) {
				throw new Error("Document has no remote workspace");
			}

			// Get access token if available.
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = getAccessTokenSilently ? await getAccessTokenSilently() : undefined;

			// Fetch the page content from DocsAPI.
			const readPageMarkdown = get($docsApi).document.page.readRaw;
			const rawMarkdown = await readPageMarkdown(
				{ workspaceName, documentId, pageId, pagePath, ref },
				token
			);

			// Parse the markdown.
			const markdown = rawMarkdown.trim().length === 0 ? "<br />" : rawMarkdown;
			const { doc } = parsePage(markdown);

			// Load the editor state.
			set($loadEditorState, { key, doc, plugins, ssr, isReadOnly: true });
		} catch (err) {
			const error = new PmRemoteLoadError(err);
			logger.error("Failed to load editor state from remote", { documentId, pageId, err });
			set($setEditorStateError, { key, error });
		}
	}
);
$loadEditorStateFromRemote.debugLabel = "loadEditorStateFromRemote";
